import { Attrs, MinzeElement } from 'minze'
import { Locale } from '../../modules/locale'
import { EventStatus } from './lvc-event-status'

Locale.getInstance().registerKeySet('fr', 'lvcEventDateStatus', {
  cancelled: 'ÉVÉNEMENT ANNULÉ',
  postponed: 'ÉVÉNEMENT REPORTÉ',
  rescheduled: 'NOUVELLE DATE',
})

Locale.getInstance().registerKeySet('en', 'lvcEventDateStatus', {
  cancelled: 'EVENT CANCELLED',
  postponed: 'EVENT POSTPONED',
  rescheduled: 'NEW DATE',
})

export interface LvcEventDate {
  text: string
  status?: EventStatus
  previousStartDate?: string
}

export class LvcEventDate extends MinzeElement {
  attrs: Attrs = [['text', ''], 'status', 'previous-start-date']

  renderIcon = (status?: EventStatus) => {
    switch (status) {
      case EventStatus.Postponed:
      case EventStatus.Cancelled:
        return whiteCalendarIcon
      default:
        return calendarIcon
    }
  }

  renderText = (text: string, status?: EventStatus) => {
    switch (status) {
      case EventStatus.Cancelled:
        return `
          <div>
            <b>${Locale.getKey('lvcEventDateStatus.cancelled')}</b>&nbsp;&nbsp;&nbsp;&nbsp;
            <s>${text}</s>
          </div>
        `
      case EventStatus.Postponed:
        return `
          <div>
            <b>${Locale.getKey('lvcEventDateStatus.postponed')}</b>&nbsp;&nbsp;&nbsp;&nbsp;
            <s>${text}</s>
          </div>
        `
      case EventStatus.Rescheduled:
        return `
          <div>
            <b>${Locale.getKey('lvcEventDateStatus.rescheduled')}</b>&nbsp;&nbsp;&nbsp;&nbsp;${text}
          </div>
        `
      default:
        return `<div>${text}</div>`
    }
  }

  html = () => {
    return /*html*/ `
      ${
        this.text
          ? `
        <div class="lvc-event-date__date-wrapper">
          ${this.renderIcon(this?.status)}
          ${this.renderText(this.text, this?.status)}
        </div>
        ${this.previousStartDate ? `<div><s>${this.previousStartDate}</s></div>` : ''}
      `
          : ''
      }
    `
  }
  css = () => /*css*/ `
    :host {
      font-size: 12px;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .lvc-event-date__date-wrapper {
      display: flex;
      gap: 4px;
      align-items: center;
      background: ${
        this.status === EventStatus.Cancelled || this.status === EventStatus.Postponed
          ? '#FF4600'
          : this.status === EventStatus.Rescheduled
            ? '#DCFFD7'
            : 'none'
      };
      padding: ${
        this.status === EventStatus.Cancelled ||
        this.status === EventStatus.Postponed ||
        this.status === EventStatus.Rescheduled
          ? '2px 10px'
          : '0'
      };
      border-radius: ${this.status === EventStatus.Cancelled || this.status === EventStatus.Postponed || this.status === EventStatus.Rescheduled ? '999px' : '0'};
      color: ${this.status === EventStatus.Cancelled || this.status === EventStatus.Postponed ? '#FFFFFF' : '#1E007D'};
    }
  `
}

const whiteCalendarIcon = `
<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M1.79297 3.62884L21.4277 3.91628M21.6568 3.83038C22.0539 5.24331 22.4012 6.87548 22.4127 8.34312C22.4127 8.37899 22.4053 8.41409 22.3914 8.4464C22.389 8.51217 22.3618 8.57476 22.3151 8.62146C22.2662 8.67037 22.1999 8.69785 22.1307 8.69785L1.93235 8.53092C1.86317 8.53092 1.79683 8.50344 1.74791 8.45453C1.71148 8.41809 1.68693 8.37198 1.67678 8.32224C1.66676 8.29462 1.66117 8.26336 1.66117 8.22844C1.7029 6.81998 1.6655 5.19746 1.58203 3.789M17.0238 9.53579C17.2259 7.92368 17.1601 4.38363 17.0238 2.48209M6.30269 2.29887L6.52026 9.47854M21.5862 22.0005L2.27132 21.5318C2.24022 21.5446 2.20665 21.5513 2.17239 21.5513C2.10321 21.5513 2.03687 21.5239 1.98796 21.4749C1.93904 21.426 1.91156 21.3597 1.91156 21.2905C2.05067 17.9172 2.05317 14.2631 1.91406 10.8827M21.4109 21.7629C21.4141 18.1583 21.5397 14.3685 21.8178 10.7844C21.8178 10.5722 21.6314 10.4948 21.4811 10.5448C15.0867 10.6628 8.43046 10.7023 2.04297 10.6327" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`
const calendarIcon = `
<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M1.79297 3.62884L21.4277 3.91628M21.6568 3.83038C22.0539 5.24331 22.4012 6.87548 22.4127 8.34312C22.4127 8.37899 22.4053 8.41409 22.3914 8.4464C22.389 8.51217 22.3618 8.57476 22.3151 8.62146C22.2662 8.67037 22.1999 8.69785 22.1307 8.69785L1.93235 8.53092C1.86317 8.53092 1.79683 8.50344 1.74791 8.45453C1.71148 8.41809 1.68693 8.37198 1.67678 8.32224C1.66676 8.29462 1.66117 8.26336 1.66117 8.22844C1.7029 6.81998 1.6655 5.19746 1.58203 3.789M17.0238 9.53579C17.2259 7.92368 17.1601 4.38363 17.0238 2.48209M6.30269 2.29887L6.52026 9.47854M21.5862 22.0005L2.27132 21.5318C2.24022 21.5446 2.20665 21.5513 2.17239 21.5513C2.10321 21.5513 2.03687 21.5239 1.98796 21.4749C1.93904 21.426 1.91156 21.3597 1.91156 21.2905C2.05067 17.9172 2.05317 14.2631 1.91406 10.8827M21.4109 21.7629C21.4141 18.1583 21.5397 14.3685 21.8178 10.7844C21.8178 10.5722 21.6314 10.4948 21.4811 10.5448C15.0867 10.6628 8.43046 10.7023 2.04297 10.6327" stroke="#1E007D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`
